import React, { useEffect, useState } from 'react'
import styles from '../ProductActivityPage.module.css'
import { apiFetch } from '../../lib/api'

// A photo a Crew phone sent for one of this cycle's events (a QC's photos, a
// pick's proof photo). The phone sends them after all its real data, so a photo
// can arrive well after its scan. The image needs the viewer's token, so it is
// fetched as a blob, never linked directly.

export interface PhotoRef {
  record: string
  idx: number
}

export const photoRefs = (record: string | null | undefined, idx: number[] | null | undefined): PhotoRef[] =>
  record && idx ? idx.map((i) => ({ record, idx: i })) : []

interface Props {
  user: { getIdToken: () => Promise<string> } | null
  photo: PhotoRef
  label: string
}

export function PhotoThumb({ user, photo, label }: Props) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let live = true
    let made: string | null = null
    apiFetch(user, `/api/product-activity/photo/${encodeURIComponent(photo.record)}/${photo.idx}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(String(r.status))
        made = URL.createObjectURL(await r.blob())
        if (live) setUrl(made)
        else URL.revokeObjectURL(made)
      })
      .catch(() => live && setFailed(true))
    return () => {
      live = false
      if (made) URL.revokeObjectURL(made)
    }
  }, [user, photo.record, photo.idx])
  if (failed) return <span className={styles.photoFail}>Photo could not be loaded</span>
  if (!url) return <span className={styles.photoWait} aria-label={`Loading ${label}`} />
  return (
    <a href={url} target="_blank" rel="noreferrer" className={styles.photoLink} title={`Open ${label}`}>
      <img src={url} alt={label} className={styles.photoImg} data-testid="cycle-photo" />
    </a>
  )
}

export function PhotoRow({ user, photos, label }: { user: Props['user']; photos: PhotoRef[]; label: string }) {
  if (!photos.length) return null
  return (
    <div className={styles.photoRow}>
      {photos.map((p) => (
        <PhotoThumb key={`${p.record}/${p.idx}`} user={user} photo={p} label={`${label} photo ${p.idx + 1}`} />
      ))}
    </div>
  )
}
